/**
 * ForgotPasswordModal.tsx — mango-web
 * PURPOSE: Modal overlay for password reset flow.
 * Mirrors Android's ForgotPasswordSheet — email input → send reset link → success state.
 */

import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

// --- Helpers ---

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// --- Component ---

/**
 * PURPOSE: Render forgot password modal with email input and success state.
 * - Connected to: stores/authStore.ts → resetPassword action
 * - Used by: routes/auth/Login.tsx
 */
export function ForgotPasswordModal({ onClose }: { onClose: () => void }) {
  const { resetPassword, isLoading, error, resetPasswordSent, clearError, clearResetPasswordSent } = useAuthStore();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // --- Handlers ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');
    clearError();

    if (!email.trim()) {
      setEmailError('please enter your email address.');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setEmailError('please enter a valid email address.');
      return;
    }

    console.debug('[ForgotPassword] sending reset email');
    await resetPassword(email);
  };

  const handleClose = () => {
    clearError();
    clearResetPasswordSent();
    onClose();
  };

  // --- Component ---

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>

        {/* Success state */}
        {resetPasswordSent ? (
          <div className="modal-success">
            <div className="modal-success-icon">✉️</div>
            <h3 className="modal-title font-display">check your email</h3>
            <p className="modal-desc">
              we sent a password reset link to <strong>{email}</strong>
            </p>
            <button className="volt-btn" onClick={handleClose}>done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 className="modal-title font-display">reset password</h3>
            <p className="modal-desc">
              enter the email associated with your account and we'll send a reset link.
            </p>

            <div className="input-group">
              <input
                type="email"
                className={`auth-input ${emailError || error ? 'auth-input-error' : ''}`}
                placeholder="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                autoFocus
                autoComplete="email"
              />
              {(emailError || error) && (
                <span className="input-error-text">{emailError || error}</span>
              )}
            </div>

            <button
              type="submit"
              className="volt-btn"
              disabled={isLoading}
            >
              <span className="btn-label">
                {isLoading ? 'sending...' : 'send reset link'}
              </span>
            </button>

            <button type="button" className="ghost-link" onClick={handleClose}>
              cancel
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// --- Related Files ---
// CONNECTED TO:
//   - src/stores/authStore.ts — resetPassword, clearResetPasswordSent
// USED BY:
//   - src/routes/auth/Login.tsx — opened on "forgot password" click
